import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Plus, 
  MoreVertical, 
  Edit, 
  Share2, 
  Download, 
  Trash2, 
  Calendar, 
  FileText, 
  CheckCircle,
  Users,
  Target,
  TrendingUp,
  Clock,
  Star,
  Filter,
  Search,
  Grid3X3,
  List,
  Eye,
  ExternalLink,
  Copy,
  Archive,
  Play,
  Pause,
  Zap,
  Sparkles,
  BarChart3,
  Activity,
  Award,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  AlertCircle,
  Info,
  Lightbulb
} from 'lucide-react';
import { Project, Task } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import SideModal, { BreadcrumbItem } from '../components/SideModal';
import ProjectDetail from '../components/ProjectDetail';
import TaskDetail from '../components/TaskDetail';
import '../index.css';

interface ProjectStats {
  total: number;
  active: number;
  completed: number;
  overdue: number;
  totalTasks: number;
  completedTasks: number;
}

const Projects: React.FC = () => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [projectNotes, setProjectNotes] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Side Modal State
  const [sideModalOpen, setSideModalOpen] = useState(false);
  const [sideModalContent, setSideModalContent] = useState<'project' | 'task'>('project');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState<ProjectStats>({
    total: 0,
    active: 0,
    completed: 0,
    overdue: 0,
    totalTasks: 0,
    completedTasks: 0
  });

  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    priority: 'medium',
    due_date: ''
  });

  useEffect(() => {
    fetchProjects();
    fetchTasks();
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('create') === 'true') {
      setShowCreateModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    calculateStats();
  }, [projects, tasks]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && !(event.target as Element).closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const calculateStats = () => {
    const now = new Date();
    const projectStats = projects.reduce((acc, project) => {
      acc.total++;
      if (project.status === 'active') acc.active++;
      if (project.status === 'completed') acc.completed++;
      if (project.due_date && new Date(project.due_date) < now && project.status !== 'completed') {
        acc.overdue++;
      }
      return acc;
    }, { total: 0, active: 0, completed: 0, overdue: 0 });

    const taskStats = tasks.reduce((acc, task) => {
      acc.totalTasks++;
      if (task.status === 'completed') acc.completedTasks++;
      return acc;
    }, { totalTasks: 0, completedTasks: 0 });

    setStats({ ...projectStats, ...taskStats });
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/projects');
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await api.get('/api/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const createProject = async () => {
    try {
      await api.post('/api/projects', newProject);
      setNewProject({ name: '', description: '', priority: 'medium', due_date: '' });
      setShowCreateModal(false);
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const updateProject = async (projectId: string, updates: any) => {
    try {
      await api.put(`/api/projects/${projectId}`, updates);
      fetchProjects();
      setShowEditModal(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project. Please try again.');
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      await api.delete(`/api/projects/${projectId}`);
      fetchProjects();
      setOpenDropdown(null);
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const shareProject = async (project: Project) => {
    const shareUrl = `${window.location.origin}/projects/${project.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Project link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
    setOpenDropdown(null);
  };

  const exportProject = async (project: Project) => {
    try {
      const projectData = {
        project,
        tasks: getProjectTasks(project.id)
      };
      
      const dataStr = JSON.stringify(projectData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name.replace(/\s+/g, '_')}_export.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting project:', error);
    }
    setOpenDropdown(null);
  };

  const editProject = (project: Project) => {
    setEditingProject(project);
    setShowEditModal(true);
    setOpenDropdown(null);
  };

  const toggleDropdown = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdown(openDropdown === projectId ? null : projectId);
  };

  const updateProjectStatus = async (projectId: string, status: string) => {
    try {
      await api.patch(`/api/projects/${projectId}`, { status });
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const getProjectTasks = (projectId: string) => {
    return tasks.filter(task => task.project_name === projects.find(p => p.id === projectId)?.name);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'from-red-500 to-pink-600';
      case 'medium': return 'from-yellow-500 to-orange-600';
      case 'low': return 'from-green-500 to-emerald-600';
      default: return 'from-slate-500 to-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'from-blue-500 to-indigo-600';
      case 'completed': return 'from-green-500 to-emerald-600';
      case 'on-hold': return 'from-yellow-500 to-orange-600';
      default: return 'from-slate-500 to-gray-600';
    }
  };

  const getProgressPercentage = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const openProjectDetail = (project: Project) => {
    setSelectedProject(project);
    setProjectNotes(project.description || '');
    setShowProjectDetail(true);
  };

  // Side Modal Functions
  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setSideModalContent('project');
    setBreadcrumbs([
      { id: 'home', title: 'Projects', type: 'home' },
      { id: project.id, title: project.name, type: 'project', data: project }
    ]);
    setSideModalOpen(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setSideModalContent('task');
    const project = projects.find(p => p.id === task.project_id);
    setBreadcrumbs([
      { id: 'home', title: 'Projects', type: 'home' },
      { id: project?.id || 'unknown', title: project?.name || 'Unknown Project', type: 'project', data: project },
      { id: task.id, title: task.title, type: 'task', data: task }
    ]);
    setSideModalOpen(true);
  };

  const handleSubProjectClick = (project: Project) => {
    setSelectedProject(project);
    setSideModalContent('project');
    const parentProject = projects.find(p => p.id === project.parent_project_id);
    setBreadcrumbs([
      { id: 'home', title: 'Projects', type: 'home' },
      { id: parentProject?.id || 'unknown', title: parentProject?.name || 'Unknown Project', type: 'project', data: parentProject },
      { id: project.id, title: project.name, type: 'subproject', data: project }
    ]);
    setSideModalOpen(true);
  };

  const handleSubTaskClick = (task: Task) => {
    setSelectedTask(task);
    setSideModalContent('task');
    const parentTask = tasks.find(t => t.id === task.parent_task_id);
    const project = projects.find(p => p.id === task.project_id);
    setBreadcrumbs([
      { id: 'home', title: 'Projects', type: 'home' },
      { id: project?.id || 'unknown', title: project?.name || 'Unknown Project', type: 'project', data: project },
      { id: parentTask?.id || 'unknown', title: parentTask?.title || 'Unknown Task', type: 'task', data: parentTask },
      { id: task.id, title: task.title, type: 'subtask', data: task }
    ]);
    setSideModalOpen(true);
  };

  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (item.type === 'home') {
      setSideModalOpen(false);
      return;
    }
    
    if (item.type === 'project' || item.type === 'subproject') {
      setSelectedProject(item.data);
      setSideModalContent('project');
    } else if (item.type === 'task' || item.type === 'subtask') {
      setSelectedTask(item.data);
      setSideModalContent('task');
    }
    
    // Update breadcrumbs to show only up to the clicked item
    const itemIndex = breadcrumbs.findIndex(b => b.id === item.id);
    if (itemIndex !== -1) {
      setBreadcrumbs(breadcrumbs.slice(0, itemIndex + 1));
    }
  };

  const handleAddTask = (projectId: string) => {
    // TODO: Implement add task functionality
    console.log('Add task to project:', projectId);
  };

  const handleAddSubProject = (parentProjectId: string) => {
    // TODO: Implement add sub project functionality
    console.log('Add sub project to project:', parentProjectId);
  };

  const handleAddSubTask = (parentTaskId: string) => {
    // TODO: Implement add sub task functionality
    console.log('Add sub task to task:', parentTaskId);
  };

  const handleStatusChange = (taskId: string, status: string) => {
    // TODO: Implement status change functionality
    console.log('Change task status:', taskId, status);
  };

  const handlePriorityChange = (taskId: string, priority: string) => {
    // TODO: Implement priority change functionality
    console.log('Change task priority:', taskId, priority);
  };

  const handleAssignUser = (taskId: string, userId: string) => {
    // TODO: Implement assign user functionality
    console.log('Assign user to task:', taskId, userId);
  };

  const deleteTask = async (taskId: string) => {
    try {
      await api.delete(`/api/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setSelectedTask(null);
      setSideModalOpen(false);
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  const saveProjectNotes = async () => {
    if (!selectedProject) return;
    
    try {
      await api.patch(`/api/projects/${selectedProject.id}`, { 
        description: projectNotes 
      });
      setShowProjectDetail(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project notes:', error);
      alert('Failed to save project notes. Please try again.');
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || project.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-800/60">
                <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded-md mt-2 animate-pulse" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white/80 dark:bg-slate-800/80 rounded-xl p-6 border border-slate-200/50 dark:border-slate-700/50">
                <div className="h-5 w-40 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-2" />
                <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-2" />
                <div className="h-3 w-2/3 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-4" />
                <div className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className={`text-3xl font-bold bg-gradient-to-r ${colors.primary} bg-clip-text text-transparent mb-2`}>
                Projects
              </h1>
              <p className={colors.textSecondary}>
                Manage your projects and track their progress with intelligent insights
              </p>
            </div>
            <button 
              onClick={() => setShowCreateModal(true)}
              className={`px-6 py-3 bg-gradient-to-r ${colors.gradient} hover:${colors.gradientHover} text-white rounded-xl transition-all duration-200 flex items-center space-x-2 font-medium shadow-lg hover:shadow-xl`}
            >
              <Plus className="w-5 h-5" />
              <span>New Project</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">{stats.total}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Total Projects</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">{stats.active}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Active</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">{stats.completed}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Completed</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">{stats.overdue}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Overdue</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'}`}
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full p-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Priority</label>
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className="w-full p-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Projects Grid/List */}
        {filteredProjects.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target className="w-12 h-12 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">
              {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' ? 'No projects found' : 'No projects yet'}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Create your first project to get started'}
            </p>
            {!searchTerm && filterStatus === 'all' && filterPriority === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all duration-200 font-medium"
              >
                Create Your First Project
              </button>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {filteredProjects.map((project) => (
              <div 
                key={project.id} 
                className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl p-6 border border-slate-200/50 dark:border-slate-700/50 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:scale-[1.02]"
                onClick={() => handleProjectClick(project)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                      {project.description || 'No description'}
                    </p>
                  </div>
                  <div className="relative dropdown-container">
                    <button 
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      onClick={(e) => toggleDropdown(project.id, e)}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openDropdown === project.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-10">
                        <div className="py-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              editProject(project);
                            }}
                            className="w-full flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <Edit className="w-4 h-4 mr-3" />
                            Edit Project
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              shareProject(project);
                            }}
                            className="w-full flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <Share2 className="w-4 h-4 mr-3" />
                            Share Project
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              exportProject(project);
                            }}
                            className="w-full flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <Download className="w-4 h-4 mr-3" />
                            Export Data
                          </button>
                          <hr className="my-2 border-slate-200 dark:border-slate-700" />
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteProject(project.id);
                            }}
                            className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                          >
                            <Trash2 className="w-4 h-4 mr-3" />
                            Delete Project
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400 mb-2">
                    <span>Progress</span>
                    <span>{getProgressPercentage(project.completed_tasks, project.task_count)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${getProgressPercentage(project.completed_tasks, project.task_count)}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {project.completed_tasks} of {project.task_count} tasks completed
                  </div>
                </div>

                {/* Project Info */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Priority:</span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getPriorityColor(project.priority)} text-white`}>
                      {project.priority}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Status:</span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getStatusColor(project.status)} text-white`}>
                      {project.status}
                    </span>
                  </div>
                  {project.due_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 dark:text-slate-400">Due Date:</span>
                      <span className="text-sm text-slate-800 dark:text-slate-200">
                        {new Date(project.due_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(project.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {getProjectTasks(project.id).length} tasks
                    </span>
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Project Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md shadow-2xl border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200">Create New Project</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter project name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</label>
                  <textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                    rows={3}
                    placeholder="Enter project description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Priority</label>
                  <select
                    value={newProject.priority}
                    onChange={(e) => setNewProject({...newProject, priority: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={newProject.due_date}
                    onChange={(e) => setNewProject({...newProject, due_date: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createProject}
                  disabled={!newProject.name.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 font-medium"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {showEditModal && editingProject && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md shadow-2xl border border-slate-200/50 dark:border-slate-700/50">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200">Edit Project</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingProject(null);
                  }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={editingProject.name}
                    onChange={(e) => setEditingProject({...editingProject, name: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter project name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</label>
                  <textarea
                    value={editingProject.description}
                    onChange={(e) => setEditingProject({...editingProject, description: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                    rows={3}
                    placeholder="Enter project description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Priority</label>
                  <select
                    value={editingProject.priority}
                    onChange={(e) => setEditingProject({...editingProject, priority: e.target.value as 'low' | 'medium' | 'high'})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Status</label>
                  <select
                    value={editingProject.status}
                    onChange={(e) => setEditingProject({...editingProject, status: e.target.value as 'active' | 'completed' | 'on-hold' | 'cancelled'})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={editingProject.due_date ? editingProject.due_date.split('T')[0] : ''}
                    onChange={(e) => setEditingProject({...editingProject, due_date: e.target.value})}
                    className="w-full p-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingProject(null);
                  }}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateProject(editingProject.id, editingProject)}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg transition-all duration-200 font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Project Detail Modal */}
        {showProjectDetail && selectedProject && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-2xl p-8 w-full max-w-6xl h-5/6 shadow-2xl border border-slate-200/50 dark:border-slate-700/50 flex flex-col">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2">{selectedProject.name}</h2>
                  <div className="flex items-center space-x-4 text-sm text-slate-600 dark:text-slate-400">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getStatusColor(selectedProject.status)} text-white`}>
                      {selectedProject.status}
                    </span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getPriorityColor(selectedProject.priority)} text-white`}>
                      {selectedProject.priority} priority
                    </span>
                    {selectedProject.due_date && (
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Due {new Date(selectedProject.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowProjectDetail(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-slate-500" />
                </button>
              </div>

              <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 overflow-hidden">
                {/* Project Notes */}
                <div className="lg:col-span-2">
                  <div className="bg-white/50 dark:bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 h-full border border-slate-200/50 dark:border-slate-600/50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center">
                        <FileText className="w-5 h-5 mr-2" />
                        Project Notes
                      </h3>
                      <button
                        onClick={saveProjectNotes}
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-sm rounded-lg transition-all duration-200 font-medium"
                      >
                        Save
                      </button>
                    </div>
                    <textarea
                      value={projectNotes}
                      onChange={(e) => setProjectNotes(e.target.value)}
                      className="w-full h-full p-4 bg-transparent border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                      placeholder="Add your project notes, ideas, and important information here..."
                    />
                  </div>
                </div>

                {/* Project Tasks */}
                <div className="bg-white/50 dark:bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50 dark:border-slate-600/50">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Tasks ({getProjectTasks(selectedProject.id).length})
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {getProjectTasks(selectedProject.id).map((task) => (
                      <div key={task.id} className="p-3 bg-white/70 dark:bg-slate-600/70 rounded-lg border border-slate-200/50 dark:border-slate-500/50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm">{task.title}</h4>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${getPriorityColor(task.priority)} text-white`}>
                                {task.priority}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${getStatusColor(task.status)} text-white`}>
                                {task.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {getProjectTasks(selectedProject.id).length === 0 && (
                      <p className="text-slate-500 dark:text-slate-400 text-sm text-center py-4">
                        No tasks in this project yet
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Side Modal */}
        <SideModal
          isOpen={sideModalOpen}
          onClose={() => setSideModalOpen(false)}
          title={sideModalContent === 'project' ? selectedProject?.name || 'Project' : selectedTask?.title || 'Task'}
          breadcrumbs={breadcrumbs}
          onBreadcrumbClick={handleBreadcrumbClick}
          size="xl"
        >
          {sideModalContent === 'project' && selectedProject && (
            <ProjectDetail
              project={selectedProject}
              tasks={tasks}
              onEdit={(project) => {
                setEditingProject(project);
                setIsEditing(true);
              }}
              onDelete={deleteProject}
              onTaskClick={handleTaskClick}
              onSubProjectClick={handleSubProjectClick}
              onAddTask={handleAddTask}
              onAddSubProject={handleAddSubProject}
              isEditing={isEditing}
              onSave={() => {
                if (editingProject) {
                  updateProject(editingProject.id, editingProject);
                  setIsEditing(false);
                  setEditingProject(null);
                }
              }}
              onCancel={() => {
                setIsEditing(false);
                setEditingProject(null);
              }}
            />
          )}
          
          {sideModalContent === 'task' && selectedTask && (
            <TaskDetail
              task={selectedTask}
              subTasks={tasks.filter(t => t.parent_task_id === selectedTask.id)}
              onEdit={(task) => {
                setSelectedTask(task);
                setIsEditing(true);
              }}
              onDelete={deleteTask}
              onSubTaskClick={handleSubTaskClick}
              onAddSubTask={handleAddSubTask}
              onStatusChange={handleStatusChange}
              onPriorityChange={handlePriorityChange}
              onAssignUser={handleAssignUser}
              isEditing={isEditing}
              onSave={() => {
                if (selectedTask) {
                  // TODO: Implement task update
                  setIsEditing(false);
                }
              }}
              onCancel={() => {
                setIsEditing(false);
              }}
            />
          )}
        </SideModal>
      </div>
    </div>
  );
};

export default Projects; 